import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { Form, Formik, setNestedObjectValues, validateYupSchema, yupToFormErrors } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import AgencySetupDropdown from '../../../components/molecules/AgencySetupDropdown';
import { AUTOCOMPLETE_LAYOUTS } from '../../../components/molecules/AutoCompleteField';
import ConfirmChangesDialog from '../../../components/organisms/NavigationBlocker/ConfirmChangesDialog';
import WarningMessage from '../../../components/utilities/WarningMessage';
import { Button, Shelf, Spinner, Tabs, WizardFooter } from '../../../components/widgets';
import tabsCss from '../../../components/widgets/Tabs.css';
import ChevronIcon from '../../../resources/icons/chevron.svg';
import GuideIcon from '../../../resources/svg/guide.svg';
import { getOrgAgencies } from '../../../selectors/agencies';
import { getAttributionAppsList } from '../../../selectors/apps';
import { getReadOnly } from '../../../selectors/permissions';
import { getAdminModeEnabled, getIsAgency, getIsUserAdmin, isUserAdNetwork } from '../../../selectors/user';
import { MessageTypes } from '../../../teamManagement/components/WizardWarningMessage';
import {
    filterAgenciesByAppPermissions,
    filterAppsByAgencyPermissions,
    getAgencyRole,
} from '../../../utils/agencyPermissions';
import { getSitePublicId } from '../../../utils/apps';
import { SAN_PARTNERS } from '../../consts';
import {
    GET_CONFIGURED_PARTNERS,
    GET_ORGANIZATION_OPTIONS,
    GET_PARTNER,
    GET_PARTNER_APP_DEFAULT_WINDOWS,
    GET_PARTNER_CONFIGURATION_FOR_APP,
    GET_PARTNER_CONFIGURATIONS_FOR_PARTNER,
    GET_POSTBACK_FILTERS,
    SAVE_CONFIGURATION,
} from '../../queries';
import { checkAgencyPermissionsForPartnerConfig, isViewMode } from '../../utils';
import { ParameterTypes, partnerShape, siteShape, TemplateTypes } from '../types';
import AdvancedSettings from './AdvancedSettings';
import AppSelection from './AppSelection';
import AttributionSettings, { getAttributionSettingsValidationSchema } from './AttributionSettings';
import DuplicateAppSelection from './DuplicateAppSelection';
import EventsSettings, { getEventsSettingsValidationSchema } from './EventsSettings';
import PartnerAdAccount, { getPartnerAdAccountValidationSchema } from './PartnerAdAccount';
import ScrollToFieldError from '../../../components/utilities/ScrollToFieldError';
import css from './PartnerConfigurationShelf.css';
import {
    getTemplate,
    getUsedPrivacyParameters,
    parsePartnerConfigurationFromServer,
    parsePartnerConfigurationToServer,
    trackPartnerConfigMixpanelEvent,
} from './utils';

export const SHELF_DATA_TEST_ID = 'partner-config-shelf';

const PARTNER_AD_ACCOUNT = 'PARTNER_AD_ACCOUNT';
const ATTRIBUTION_SETTINGS = 'ATTRIBUTION_SETTINGS';
const EVENTS_SETTINGS = 'EVENTS_SETTINGS';
const ADVANCED_SETTINGS = 'ADVANCED_SETTINGS';
const AGENCY_SETUP_HEADER_CLASS = 'agencySetupHeader';

const SHELF_PAGES = [
    {
        id: PARTNER_AD_ACCOUNT,
        label: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.TITLE',
    },
    {
        id: ATTRIBUTION_SETTINGS,
        label: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.ATTRIBUTION_SETTINGS.HEADER',
    },
    {
        id: EVENTS_SETTINGS,
        label: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.EVENTS_SETTINGS',
    },
    {
        id: ADVANCED_SETTINGS,
        label: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.ADVANCED_SETTINGS.HEADER',
    },
];

const PAGE_COMPONENTS = {
    [PARTNER_AD_ACCOUNT]: PartnerAdAccount,
    [ATTRIBUTION_SETTINGS]: AttributionSettings,
    [EVENTS_SETTINGS]: EventsSettings,
    [ADVANCED_SETTINGS]: AdvancedSettings,
};

export const SHELF_MODE = {
    CREATE: 'Create',
    EDIT: 'Edit',
    DUPLICATE: 'Duplicate',
    VIEW: 'View',
};

const isSaveButtonDisabled = (selectedPage, mode) =>
    selectedPage === PARTNER_AD_ACCOUNT && ![SHELF_MODE.EDIT, SHELF_MODE.DUPLICATE].includes(mode);

const getFooterButtons = (
    shelfPages,
    selectedPage,
    isSaving,
    isReadonly,
    mode,
    isSaveDisabled,
    onClose,
    onPageChanged,
    onSaveClicked
) => {
    const isFirstPage = selectedPage === shelfPages[0].id;
    const isLastPage = selectedPage === shelfPages[shelfPages.length - 1].id;
    const getClassName = disabled => classNames(css.footerBtn, { [css.disabled]: disabled });

    const saveButtonTextByMode = {
        [SHELF_MODE.CREATE]: 'SAVE',
        [SHELF_MODE.EDIT]: 'SAVE',
        [SHELF_MODE.DUPLICATE]: 'DUPLICATE',
        [SHELF_MODE.VIEW]: 'DONE',
    };

    const saveButtonProps = isReadonly
        ? { onClick: onClose }
        : { onClick: onSaveClicked, disabled: isSaving || isSaveDisabled, showSpinner: isSaving };

    const buttons = [];

    if (!isReadonly) {
        buttons.push(
            <Button type="flat" onClick={onClose} className={css.cancelButton}>
                <Translate id="STATIC.BUTTONS.CANCEL" />
            </Button>
        );
    }

    return [
        ...buttons,
        <Button type="secondary" className={getClassName(isFirstPage)} onClick={() => onPageChanged(false)}>
            <>
                <ChevronIcon className={classNames(css.arrow, css.back)} />
                <Translate id="STATIC.BUTTONS.BACK" />
            </>
        </Button>,
        <Button type="secondary" className={getClassName(isLastPage)} onClick={() => onPageChanged(true)}>
            <>
                <Translate id="STATIC.BUTTONS.NEXT" />
                <ChevronIcon className={classNames(css.arrow, css.next)} />
            </>
        </Button>,
        <Button {...saveButtonProps}>
            <Translate id={`STATIC.BUTTONS.${isReadonly ? 'DONE' : saveButtonTextByMode[mode]}`} />
        </Button>,
    ];
};

const getValidationSchemaByTab = (partner, platform) => ({
    [PARTNER_AD_ACCOUNT]: getPartnerAdAccountValidationSchema(partner),
    [ATTRIBUTION_SETTINGS]: getAttributionSettingsValidationSchema(partner, platform),
    [EVENTS_SETTINGS]: getEventsSettingsValidationSchema(partner, platform),
});

const containsAdvancedFeatures = (partner, platform, organizationOptions) => {
    const partnerWithPrivacyParameters =
        partner &&
        platform &&
        getUsedPrivacyParameters(
            partner,
            platform,
            getTemplate(partner, platform, TemplateTypes.INSTALL) ? TemplateTypes.INSTALL : TemplateTypes.CUSTOM,
            [ParameterTypes.ACCOUNT, ParameterTypes.APPLICATION]
        ).length > 0;
    return (
        organizationOptions.ldsSupported ||
        organizationOptions.geoRestrictionsSupported ||
        organizationOptions.attributionReturnRateSupported ||
        partnerWithPrivacyParameters
    );
};

const PartnerConfigurationShelf = ({
    open = false,
    partner,
    siteId = null,
    agencyId = null,
    apps = [],
    mode: paramMode = SHELF_MODE.CREATE,
    onClose = () => {},
    loading: shelfLoading = false,
}) => {
    const readOnly = useSelector(getReadOnly);

    const [selectedPage, setSelectedPage] = useState(ATTRIBUTION_SETTINGS);
    const [selectedSiteId, setSelectedSiteId] = useState(siteId);
    const [duplicatedSiteId, setDuplicatedSiteId] = useState(null);
    const [selectedAgencyId, setSelectedAgencyId] = useState(agencyId);
    const [warnings, setWarnings] = useState([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const formRef = useRef(null);
    const tabContentRef = useRef(null);
    const [scrollSignal, setScrollSignal] = useState(0);
    const isAgency = useSelector(state => getIsAgency(state));
    const isPartner = useSelector(state => isUserAdNetwork(state));
    const isUserAdmin = useSelector(state => getIsUserAdmin(state));
    const isAdminMode = useSelector(state => getAdminModeEnabled(state));
    const orgAgencies = useSelector(getOrgAgencies);
    const orgAgenciesLoaded = !!orgAgencies;
    const appsList = useSelector(getAttributionAppsList);

    const [getPartner, partnerResponse] = useLazyQuery(GET_PARTNER);
    const [getPartnerConfiguration, partnerConfigurationResponse] = useLazyQuery(GET_PARTNER_CONFIGURATION_FOR_APP);
    const [savePartnerConfiguration, savePartnerConfigurationResponse] = useMutation(SAVE_CONFIGURATION);
    const configuredPartnersResponse = useQuery(GET_CONFIGURED_PARTNERS);
    const [, partnerConfigurationsResponse] = useLazyQuery(GET_PARTNER_CONFIGURATIONS_FOR_PARTNER);

    const [getPartnerWindows] = useLazyQuery(GET_PARTNER_APP_DEFAULT_WINDOWS);
    const organizationOptionsResponse = useQuery(GET_ORGANIZATION_OPTIONS);
    const organizationOptions = organizationOptionsResponse.data || {};
    const supportPostbackFilters = organizationOptions?.postbackFiltersSupported;
    const postbackFiltersResponse = useQuery(GET_POSTBACK_FILTERS, { skip: !supportPostbackFilters });
    const availableFilters = postbackFiltersResponse.data?.filters || [];

    const partnerDetails = partnerResponse?.data?.partner;
    const { guideLink, postbackTemplates } = partnerDetails || {};
    const filteredApps = useMemo(() => {
        if (!selectedAgencyId) {
            return apps;
        }

        const agencyRole = getAgencyRole(orgAgencies, selectedAgencyId);
        return filterAppsByAgencyPermissions(apps, agencyRole);
    }, [apps, selectedAgencyId, orgAgencies]);

    const sites = filteredApps.map(app => app.sites).flat();
    // Use appsList (from Redux) to find sites for permission checks and when site might be filtered out
    const allSitesFromRedux = appsList?.map(app => app.sites).flat() || [];
    const selectedSite = allSitesFromRedux.find(site => site.site_id === selectedSiteId);
    const duplicatedSite = duplicatedSiteId ? sites.find(site => site.site_id === duplicatedSiteId) : null;
    const platform = (duplicatedSite || selectedSite)?.platform;

    const configuredPartners = configuredPartnersResponse?.data?.configuredPartners || [];
    const editedPartnerConfiguration = partnerConfigurationResponse?.data?.partnerConfiguration;
    const isCustomConfig = !!editedPartnerConfiguration?.isCustom;
    const isSAN = SAN_PARTNERS.includes(partner.name);
    const enableSanForAgency = (isUserAdmin && isAdminMode) || organizationOptions?.sanPartnersForAgencySupported;
    const isAgencySanRestricted = isAgency && isSAN && !enableSanForAgency;
    const sanViewOnlyForAgency = isAgency && isSAN && organizationOptions?.sanPartnersViewOnlyForAgencySupported;
    const canSelectAgency = !isPartner && !isAgency && !isSAN;
    const selectedApp = filteredApps.find(app => app.sites.some(site => site.site_id === selectedSiteId));
    const selectedAppSites = selectedSiteId ? selectedApp?.sites || [] : null;
    // In duplicate mode, use the "TO" app sites for agency filtering (not the "FROM" app)
    const duplicatedApp = duplicatedSiteId
        ? filteredApps.find(app => app.sites.some(site => site.site_id === duplicatedSiteId))
        : null;
    const duplicatedAppSites = duplicatedSiteId ? duplicatedApp?.sites || [] : null;

    // Check if advertiser is viewing agency config without permissions (for warning and sites display)
    const agencyConfigWithoutPermissions = useMemo(() => {
        if (isAgency || isPartner) {
            return false;
        }
        const configAgencyId = editedPartnerConfiguration?.agencyId || agencyId;
        const configSiteId = editedPartnerConfiguration?.siteId || siteId;

        if (!configAgencyId || !configSiteId || !orgAgencies || !appsList?.length) {
            return false;
        }

        const siteForCheck = allSitesFromRedux.find(site => site.site_id === configSiteId);
        // If site not found in allSitesFromRedux, agency doesn't have permission to this app
        if (!siteForCheck) {
            return true;
        }

        const hasPermissions = checkAgencyPermissionsForPartnerConfig(
            orgAgencies,
            configAgencyId,
            configSiteId,
            siteForCheck.longname,
            siteForCheck.platform,
            partner.name,
            appsList
        );

        return !hasPermissions;
    }, [
        isAgency,
        isPartner,
        agencyId,
        siteId,
        editedPartnerConfiguration,
        orgAgencies,
        appsList,
        allSitesFromRedux,
        partner.name,
    ]);

    const mode =
        isViewMode(editedPartnerConfiguration, isAgency, readOnly, orgAgencies, appsList, partner.name) ||
        isCustomConfig ||
        isAgencySanRestricted ||
        sanViewOnlyForAgency
            ? SHELF_MODE.VIEW
            : paramMode;

    const isReadonly = mode === SHELF_MODE.VIEW;

    const filteredShelfPages = useMemo(() => {
        return SHELF_PAGES.filter(page => {
            if (page.id === PARTNER_AD_ACCOUNT && !partnerDetails?.accountsSupport) {
                return false;
            }
            return !(
                page.id === ADVANCED_SETTINGS &&
                !containsAdvancedFeatures(partnerDetails, platform, organizationOptions)
            );
        });
    }, [partnerDetails?.accountsSupport, partnerDetails, platform, organizationOptions]);

    const isPartnerForAgency = useMemo(() => {
        return selectedAgencyId && !isAgency;
    }, [selectedAgencyId, isAgency]);

    const PageComponent = PAGE_COMPONENTS[selectedPage];

    const tabContentProps = {
        partner: partnerDetails,
        site: duplicatedSite || selectedSite,
        orgOptions: organizationOptions,
        isReadonly,
        availableFilters,
    };

    let initialValues =
        editedPartnerConfiguration && selectedSiteId === editedPartnerConfiguration.siteId
            ? parsePartnerConfigurationFromServer(
                  editedPartnerConfiguration,
                  partnerDetails,
                  platform,
                  availableFilters
              )
            : { site: selectedSiteId || undefined };

    if (mode === SHELF_MODE.DUPLICATE) {
        initialValues = { ...initialValues, site: '' };
    }

    // Reset selected site when agency changes and current site is not in filtered list
    useEffect(() => {
        if (
            (mode === SHELF_MODE.CREATE || mode === SHELF_MODE.DUPLICATE) &&
            selectedSiteId &&
            !sites.find(site => site.site_id === selectedSiteId)
        ) {
            setSelectedSiteId(null);
        }
    }, [selectedAgencyId, sites, selectedSiteId, mode]);

    // Clear selected agency if partner is SAN (advertisers can't add agency as owner for SANs)
    useEffect(() => {
        if ((mode === SHELF_MODE.CREATE || mode === SHELF_MODE.DUPLICATE) && isSAN && selectedAgencyId) {
            setSelectedAgencyId(null);
        }
    }, [isSAN, mode, selectedAgencyId]);

    // Reset selected agency when app changes and current agency doesn't have permission for new app
    useEffect(() => {
        // In duplicate mode, use duplicatedAppSites (TO app), otherwise use selectedAppSites
        const appSitesToCheck = mode === SHELF_MODE.DUPLICATE ? duplicatedAppSites : selectedAppSites;

        if (
            ![SHELF_MODE.CREATE, SHELF_MODE.DUPLICATE].includes(mode) ||
            !selectedAgencyId ||
            !appSitesToCheck?.length ||
            !orgAgencies?.length
        ) {
            return;
        }

        // Only check permissions if we have valid app sites with publicId
        const sitesWithPublicId = appSitesToCheck.filter(site => {
            const publicId = getSitePublicId(site);
            return publicId && publicId.trim() !== '';
        });

        if (!sitesWithPublicId.length) {
            return; // Can't check permissions without publicId
        }

        const agencyRole = getAgencyRole(orgAgencies, selectedAgencyId);
        if (agencyRole) {
            const filteredAgencies = filterAgenciesByAppPermissions(orgAgencies, sitesWithPublicId);
            const agencyPermission = filteredAgencies.find(({ agency }) => agency.agency_id === selectedAgencyId);
            if (!agencyPermission?.hasPermission) {
                setSelectedAgencyId(null);
            }
        }
    }, [selectedSiteId, duplicatedSiteId, selectedAppSites, duplicatedAppSites, selectedAgencyId, orgAgencies, mode]);

    const addWarning = newWarning => {
        const warningWithId = { ...newWarning, id: Date.now() };

        setWarnings(prev => {
            // Check if we already have this exact error message
            return prev.some(w => w.type === newWarning.type && w.message === newWarning.message)
                ? prev
                : [...prev, warningWithId];
        });
    };

    const removeWarning = warningId => {
        setWarnings(prev => {
            return prev.filter(w => w.id !== warningId);
        });
    };

    useEffect(() => {
        if (isAgencySanRestricted || sanViewOnlyForAgency) {
            addWarning({
                type: MessageTypes.WARNING,
                message: sanViewOnlyForAgency
                    ? 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.AGENCY_SAN_VIEW_ONLY_MESSAGE'
                    : 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.AGENCY_CANNOT_EDIT_SAN_MESSAGE',
                textReplace: { partnerName: partner.displayName },
                showIcon: false,
            });
        }
    }, [isAgencySanRestricted, partner.displayName, sanViewOnlyForAgency]);

    useEffect(() => {
        if (partnerDetails) {
            setSelectedPage(filteredShelfPages[0].id);
        }
    }, [partnerDetails?.accountsSupport]);

    const schemasByTab = useMemo(() => (partnerDetails ? getValidationSchemaByTab(partnerDetails, platform) : {}), [
        partnerDetails,
        platform,
    ]);

    // Page-scoped: always validate site + accounts, plus the current tab's slice (gates navigation).
    const validationSchema = useMemo(() => {
        if (!partnerDetails || isReadonly) return null;
        return Yup.object().shape({
            site: Yup.string().required('STATIC.REQUIRED_FIELD'),
            ...schemasByTab[PARTNER_AD_ACCOUNT],
            ...(schemasByTab[selectedPage] || {}),
        });
    }, [schemasByTab, selectedPage, partnerDetails, isReadonly]);

    // Page-independent: validate every tab at once (used on Save).
    const fullValidationSchema = useMemo(() => {
        if (!partnerDetails || isReadonly) return null;
        return Yup.object().shape({
            site: Yup.string().required('STATIC.REQUIRED_FIELD'),
            ...Object.assign({}, ...Object.values(schemasByTab)),
        });
    }, [schemasByTab, partnerDetails, isReadonly]);

    // Resolve which tab owns the first error, in rendered tab order.
    const resolveFirstErroredTab = errors => {
        const errorKeys = Object.keys(errors);
        const tab = filteredShelfPages.find(({ id }) =>
            Object.keys(schemasByTab[id] || {}).some(key => errorKeys.includes(key))
        );
        return tab?.id || filteredShelfPages[0].id;
    };

    useEffect(() => {
        if (!open) return;
        getPartner({ variables: { partnerId: partner.id } });
    }, [open, partner.id]);

    useEffect(() => {
        if (!open || !selectedSite) return;
        getPartnerWindows({
            variables: { partnerId: partner.id, longname: selectedSite.longname, platform: selectedSite.platform },
        });
    }, [open, partner.id, selectedSite?.longname, selectedSite?.platform]);

    useEffect(() => {
        if (!open || !selectedSite) return;
        // Only refetch configuration when site changes or when editing (to get agency-specific config)
        getPartnerConfiguration({
            variables: {
                partnerId: partner.id,
                longname: selectedSite.longname,
                platform: selectedSite.platform,
                agencyId: selectedAgencyId || null,
            },
        });
    }, [open, partner.id, selectedSite?.longname, selectedSite?.platform, selectedAgencyId]);

    useEffect(() => {
        const { called, loading, data } = savePartnerConfigurationResponse;
        if (!called || loading) return;

        if (data?.savePartnerConfiguration?.ok) {
            partnerConfigurationsResponse.refetch({ partnerId: partner.id });
            configuredPartnersResponse.refetch();
            partnerConfigurationResponse.refetch({
                partnerId: partner.id,
                longname: selectedSite.longname,
                platform: selectedSite.platform,
                agencyId: selectedAgencyId || null,
            });
            onClose();
        } else {
            addWarning({
                type: MessageTypes.ERROR,
                message: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.SAVING_FAILURE_MESSAGE',
            });
        }

        savePartnerConfigurationResponse.reset();
    }, [savePartnerConfigurationResponse]);

    const onSiteChanged = id => {
        if (mode === SHELF_MODE.DUPLICATE) {
            setDuplicatedSiteId(id);
            return;
        }

        setSelectedSiteId(id);
        setSelectedPage(filteredShelfPages[0].id);
    };

    const getPartnerConfigurationPayload = values => {
        // For agencies, use their agencyId; for advertisers, use selected agency or existing config's agency
        const effectiveAgencyId = isAgency ? agencyId : selectedAgencyId || editedPartnerConfiguration?.agencyId;
        return parsePartnerConfigurationToServer(partner, duplicatedSite || selectedSite, values, effectiveAgencyId);
    };

    // Several tab components seed/normalize Formik values on mount, those writes flip Formik's `dirty` flag
    const hasUnsavedChanges = () => {
        const form = formRef.current;
        if (!form || isReadonly) return false;

        // Without a selected site there is no payload to compare yet; fall back to Formik.
        if (!(duplicatedSite || selectedSite)) return form.dirty;

        try {
            return !isEqual(
                getPartnerConfigurationPayload(form.initialValues),
                getPartnerConfigurationPayload(form.values)
            );
        } catch (e) {
            return form.dirty;
        }
    };

    const handleRequestClose = () => {
        if (hasUnsavedChanges()) {
            setShowCancelConfirm(true);
            return;
        }
        onClose();
    };

    const onGuideClick = () => {
        trackPartnerConfigMixpanelEvent('Shelf Guide Clicked', partner, selectedSite);
        window.open(guideLink, '_blank', 'noreferrer');
    };

    const validateForm = async form => {
        const errors = await form.validateForm();
        if (Object.keys(errors).length === 0) {
            return true;
        }

        form.setTouched(setNestedObjectValues(errors, true));
        return false;
    };

    const onPageChanged = async (isNextPage, form) => {
        const isFormValid = await validateForm(form);
        if (!isFormValid) return;

        const step = isNextPage ? 1 : -1;
        const index = filteredShelfPages.findIndex(({ id }) => id === selectedPage);
        const nextPage = filteredShelfPages[index + step].id;
        setSelectedPage(nextPage);

        const buttonName = isNextPage ? 'Next' : 'Back';
        trackPartnerConfigMixpanelEvent(`Shelf ${buttonName} Clicked`, partner, selectedSite, { mode });
    };

    const onTabClicked = async (tabId, form) => {
        const isFormValid = await validateForm(form);
        if (isFormValid) setSelectedPage(tabId);
    };

    const onSaveClicked = async form => {
        const errors = await validateYupSchema(form.values, fullValidationSchema)
            .then(() => ({}))
            .catch(err => yupToFormErrors(err));

        if (Object.keys(errors).length === 0) {
            form.submitForm();
            return;
        }

        form.setTouched(setNestedObjectValues(errors, true));
        const targetTab = resolveFirstErroredTab(errors);
        if (targetTab !== selectedPage) setSelectedPage(targetTab);
        setScrollSignal(s => s + 1); // the scrollSignal effect scrolls after the render commits
    };

    const onSave = async partnerConfigurationValues => {
        const partnerConfiguration = getPartnerConfigurationPayload(partnerConfigurationValues);

        try {
            trackPartnerConfigMixpanelEvent('Save', partner, selectedSite, {
                mode,
                isAgency,
                agencyId: partnerConfiguration.agencyId,
            });
            await savePartnerConfiguration({ variables: { partnerConfiguration } });
        } catch (error) {
            addWarning({
                type: MessageTypes.ERROR,
                message: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.SAVING_FAILURE_MESSAGE',
            });
        }
    };

    const headerKey = isPartnerForAgency
        ? `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.${
              mode === SHELF_MODE.DUPLICATE ? 'DUPLICATE_AGENCY_MODE' : 'HEADER_AGENCY_MODE'
          }`
        : `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.${mode === SHELF_MODE.DUPLICATE ? 'DUPLICATE' : 'HEADER'}`;

    const shouldShowSpinner = useMemo(() => {
        return (
            partnerResponse.loading ||
            shelfLoading ||
            (!isAgency && !isPartner && !orgAgenciesLoaded) ||
            (partnerResponse.called && !partnerResponse.loading && !partnerDetails)
        );
    }, [partnerResponse, shelfLoading, isAgency, isPartner, orgAgenciesLoaded, partnerDetails]);

    return (
        <Shelf
            open={open}
            shelfSize="xxlarge"
            headerText={headerKey}
            headerClassName={isPartnerForAgency ? AGENCY_SETUP_HEADER_CLASS : ''}
            headerData={{ partnerName: partner.displayName }}
            headerImagePath={partner.logoPath}
            enterAnimationDisabled={false}
            onClose={handleRequestClose}
            dataTestId={SHELF_DATA_TEST_ID}
        >
            {shouldShowSpinner ? (
                <Spinner show expanded />
            ) : (
                <Formik
                    innerRef={formRef}
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSave}
                >
                    {form => (
                        <Form className={css.partnerConfigurationShelf}>
                            <Tabs
                                tabs={filteredShelfPages}
                                selected={selectedPage}
                                containerClass={tabsCss.shelfTabs}
                                onClick={tabId => onTabClicked(tabId, form)}
                                numbered
                            />
                            {warnings.map(warning => (
                                <WarningMessage
                                    key={warning.id}
                                    show
                                    showIcon={warning.showIcon ?? true}
                                    type={warning.type}
                                    message={warning.message}
                                    textReplace={warning.textReplace}
                                    duration={warning.duration || 1000}
                                    onClose={() => removeWarning(warning.id)}
                                />
                            ))}
                            {agencyConfigWithoutPermissions && (
                                <WarningMessage
                                    show
                                    showIcon={false}
                                    type={MessageTypes.WARNING}
                                    message="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.AGENCY_NO_PERMISSIONS_WARNING"
                                />
                            )}
                            <div className={css.tabContent} ref={tabContentRef}>
                                <div
                                    className={classNames(css.topArea, {
                                        [css.duplicateMode]: mode === SHELF_MODE.DUPLICATE,
                                    })}
                                >
                                    {canSelectAgency && (
                                        <AgencySetupDropdown
                                            value={selectedAgencyId}
                                            onChange={setSelectedAgencyId}
                                            selectedAppSites={
                                                mode === SHELF_MODE.DUPLICATE ? duplicatedAppSites : selectedAppSites
                                            }
                                            selectedPartnerName={partner.name}
                                            configuredPartners={configuredPartners}
                                            hideTooltip
                                            labelClassName={css.fixedLabelWidth}
                                            layout={AUTOCOMPLETE_LAYOUTS.HORIZONTAL}
                                            containerClassName={
                                                mode === SHELF_MODE.DUPLICATE ? css.duplicateModeAgencyContainer : ''
                                            }
                                            disabled={mode === SHELF_MODE.EDIT || mode === SHELF_MODE.VIEW}
                                        />
                                    )}
                                    {mode === SHELF_MODE.DUPLICATE ? (
                                        <DuplicateAppSelection
                                            duplicatedSite={selectedSite}
                                            partner={partner}
                                            sites={sites}
                                            postbackTemplates={postbackTemplates}
                                            onChange={onSiteChanged}
                                            onWarning={addWarning}
                                        />
                                    ) : (
                                        <AppSelection
                                            disabled={mode !== SHELF_MODE.CREATE}
                                            partner={partner}
                                            sites={agencyConfigWithoutPermissions ? allSitesFromRedux : sites}
                                            postbackTemplates={postbackTemplates}
                                            onChange={onSiteChanged}
                                            layout={AUTOCOMPLETE_LAYOUTS.HORIZONTAL}
                                            selectedAgencyId={selectedAgencyId}
                                            showEmptyState={!sanViewOnlyForAgency && !isAgencySanRestricted}
                                        />
                                    )}
                                    {!guideLink ? null : (
                                        <div className={css.guide} onClick={onGuideClick}>
                                            <GuideIcon className={css.icon} />
                                            <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_GUIDE" />
                                        </div>
                                    )}
                                </div>
                                {partnerConfigurationResponse.loading ? (
                                    <Spinner show expanded />
                                ) : (
                                    selectedSite && <PageComponent {...tabContentProps} />
                                )}
                            </div>
                            <ScrollToFieldError containerRef={tabContentRef} trigger={scrollSignal} />
                            <WizardFooter
                                buttons={getFooterButtons(
                                    filteredShelfPages,
                                    selectedPage,
                                    form.isSubmitting,
                                    isReadonly,
                                    mode,
                                    isSaveButtonDisabled(selectedPage, mode),
                                    handleRequestClose,
                                    isNextPage => onPageChanged(isNextPage, form),
                                    () => onSaveClicked(form)
                                )}
                            />
                        </Form>
                    )}
                </Formik>
            )}
            <ConfirmChangesDialog
                open={showCancelConfirm}
                title="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.UNSAVED_CHANGES_TITLE"
                text="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.UNSAVED_CHANGES_MESSAGE"
                acceptText="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.UNSAVED_CHANGES_ACCEPT"
                rejectText="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.UNSAVED_CHANGES_REJECT"
                onAccept={() => {
                    setShowCancelConfirm(false);
                    onClose();
                }}
                onReject={() => setShowCancelConfirm(false)}
            />
        </Shelf>
    );
};

PartnerConfigurationShelf.propTypes = {
    open: PropTypes.bool,
    partner: partnerShape.isRequired,
    siteId: PropTypes.number,
    agencyId: PropTypes.string,
    apps: PropTypes.arrayOf(PropTypes.shape({ sites: PropTypes.arrayOf(siteShape) })),
    mode: PropTypes.oneOf(Object.values(SHELF_MODE)),
    loading: PropTypes.bool,
    onClose: PropTypes.func,
};

export default PartnerConfigurationShelf;
